import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useDeviceDetail, useUpdateDevice, useDeleteDevice, useAssignUserToDevice, useUnassignUserFromDevice, useSetPrimaryUser,
  useDeleteApplication, useDeleteMacDrive, useDeleteBrowserExtension, useDeleteScreenlockPolicy,
  useDeleteUserPolicy, useDeleteProgram, useDeleteWindowsDrive, useDeleteSecurityProduct,
  useDeleteSecurityCenter,
} from "@/api/devices";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/stores/auth-store";
import { useVantaStatus } from "@/api/vanta";
import { useTenantUsers } from "@/api/tenant-users";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { ComplianceBadge } from "@/components/compliance-badge";
import { AddUserDialog } from "@/components/add-user-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApplicationModal } from "./modals/application-modal";
import { MacDriveModal } from "./modals/mac-drive-modal";
import { BrowserExtensionModal } from "./modals/browser-extension-modal";
import { ScreenlockPolicyModal } from "./modals/screenlock-policy-modal";
import { UserPolicyModal } from "./modals/user-policy-modal";
import { ProgramModal } from "./modals/program-modal";
import { WindowsDriveModal } from "./modals/windows-drive-modal";
import { SecurityProductModal } from "./modals/security-product-modal";
import { SecurityCenterModal } from "./modals/security-center-modal";
import { DeleteResourceDialog } from "./modals/delete-resource-dialog";
import type {
  Device, DeviceApplication, DeviceBrowserExtension, DeviceProgram,
  DeviceScreenlockPolicy, DeviceSource, DeviceUserAssignment,
  MacDeviceDrive, UserDevicePolicy, WindowsDeviceDrive, WindowsSecurityProduct,
} from "@/api/types";

const SOURCE_KEYS = ["crowdstrike", "atera", "jumpcloud"] as const;

const COMPARISON_FIELDS: { label: string; accessor: (s: DeviceSource) => string }[] = [
  { label: "Hostname", accessor: (s) => s.hostname || "\u2014" },
  { label: "Serial Number", accessor: (s) => s.serial_number || "\u2014" },
  { label: "Platform", accessor: (s) => s.platform || "\u2014" },
  { label: "OS Version", accessor: (s) => s.os_version || "\u2014" },
  { label: "Last Seen", accessor: (s) => s.last_seen ? new Date(s.last_seen).toLocaleDateString() : "\u2014" },
  { label: "IP Address", accessor: (s) => s.ip_address || "\u2014" },
];

export function DeviceDetailPage() {
  const { tenantId, deviceId } = useParams<{ tenantId: string; deviceId: string }>();
  const { data: device, isLoading } = useDeviceDetail(tenantId!, deviceId!);
  const { data: vantaStatus } = useVantaStatus(tenantId!);
  const vantaConnected = vantaStatus?.connected === true;
  const { data: usersData } = useTenantUsers(tenantId!, { pageSize: "200" });
  const assignUser = useAssignUserToDevice(tenantId!, deviceId!);
  const unassignUser = useUnassignUserFromDevice(tenantId!, deviceId!);
  const setPrimary = useSetPrimaryUser(tenantId!, deviceId!);
  const { user } = useAuth();
  const navigate = useNavigate();
  const deleteDevice = useDeleteDevice(tenantId!, deviceId!);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addUserOpen, setAddUserOpen] = useState(false);

  const canDelete = !!user && (user.role === "super_admin" || user.role === "operator");

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>;
  if (!device) return <p className="text-text-muted">Device not found.</p>;

  const sourceMap = new Map(device.sources?.map((s) => [s.integration_source, s]));
  const linkedSources = SOURCE_KEYS.filter((k) => sourceMap.has(k));
  const assignedUserIds = new Set(device.user_assignments?.map((ua) => ua.user));
  const availableUsers = usersData?.data?.filter((u) => !assignedUserIds.has(u.id)) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{device.hostname || device.nickname || "Unnamed Device"}</h1>
          <p className="text-sm text-text-muted">{device.serial_number} &middot; {device.platform} &middot; {device.os_version}</p>
        </div>
        <ComplianceBadge score={device.compliance_score} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Device Info</CardTitle></CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-text-muted">Type</dt><dd>{device.device_type || "N/A"}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Platform</dt><dd>{device.platform || "N/A"}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">OS Version</dt><dd>{device.os_version || "N/A"}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Last Seen</dt><dd>{device.last_seen ? new Date(device.last_seen).toLocaleString() : "Never"}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Tags</dt><dd>{device.tags?.join(", ") || "None"}</dd></div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Compliance</CardTitle></CardHeader>
          <CardContent>
            <div className="mb-4 text-center">
              <p className="text-4xl font-bold">{device.compliance_score ?? "N/A"}<span className="text-lg text-text-muted">%</span></p>
            </div>
            {device.compliance_reasons && device.compliance_reasons.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-text-muted uppercase">Reason Codes</p>
                {device.compliance_reasons.map((reason, i) => (
                  <p key={i} className="text-sm text-warning">{reason}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {linkedSources.length > 0 && (
        <div className={`grid gap-4 ${linkedSources.length === 1 ? "md:grid-cols-1" : linkedSources.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          {linkedSources.map((source) => {
            const s = sourceMap.get(source)!;
            return (
              <Card key={source}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="capitalize">{source}</span>
                    <Badge variant="success">Linked</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between"><dt className="text-text-muted">Source ID</dt><dd className="truncate max-w-[150px]">{s.source_id}</dd></div>
                    <div className="flex justify-between"><dt className="text-text-muted">Hostname</dt><dd>{s.hostname}</dd></div>
                    <div className="flex justify-between"><dt className="text-text-muted">Last Seen</dt><dd>{s.last_seen ? new Date(s.last_seen).toLocaleDateString() : "N/A"}</dd></div>
                    <div className="flex justify-between"><dt className="text-text-muted">IP</dt><dd>{s.ip_address || "N/A"}</dd></div>
                  </dl>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {linkedSources.length >= 2 && (
        <Card>
          <CardHeader><CardTitle>Source Comparison</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 pr-4 text-left font-medium text-text-muted">Field</th>
                    {linkedSources.map((key) => (
                      <th key={key} className="py-2 px-4 text-left font-medium capitalize text-text-muted">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_FIELDS.map(({ label, accessor }) => {
                    const values = linkedSources.map((key) => accessor(sourceMap.get(key)!));
                    const nonEmpty = values.filter((v) => v !== "\u2014");
                    const hasMismatch = nonEmpty.length >= 2 && new Set(nonEmpty).size > 1;

                    return (
                      <tr key={label} className="border-b border-border/50">
                        <td className="py-2 pr-4 font-medium text-text-muted">{label}</td>
                        {values.map((val, i) => (
                          <td
                            key={i}
                            className={`py-2 px-4 ${
                              hasMismatch && val !== "\u2014" ? "text-warning" : ""
                            }`}
                          >
                            {val}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <VantaResourceCard device={device} tenantId={tenantId!} deviceId={deviceId!} vantaConnected={vantaConnected} />

      <Card>
        <CardHeader><CardTitle>Assigned Users</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {device.user_assignments && device.user_assignments.length > 0 ? (
              device.user_assignments.map((ua) => (
                <div key={ua.id} className="flex items-center justify-between rounded-lg p-2 hover:bg-surface-hover">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{ua.email}</span>
                    {ua.is_primary && <Badge variant="accent">Primary</Badge>}
                  </div>
                  <div className="flex gap-2">
                    {!ua.is_primary && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPrimary.mutate(ua.user)}
                        disabled={setPrimary.isPending}
                      >
                        Set Primary
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => unassignUser.mutate(ua.user)}
                      disabled={unassignUser.isPending}
                      className="text-danger hover:text-danger"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-2 text-sm text-text-muted">No users assigned.</p>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="flex-1"
            >
              <option value="">Select user to assign...</option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.email}</option>
              ))}
            </Select>
            <Button
              onClick={() => {
                if (selectedUserId) {
                  assignUser.mutate({ user_id: selectedUserId }, { onSuccess: () => setSelectedUserId("") });
                }
              }}
              disabled={!selectedUserId || assignUser.isPending}
            >
              {assignUser.isPending ? "Assigning..." : "Assign"}
            </Button>
            <Button variant="outline" onClick={() => setAddUserOpen(true)}>
              Add User
            </Button>
          </div>
        </CardContent>
      </Card>

      {addUserOpen && (
        <AddUserDialog
          open
          onClose={() => setAddUserOpen(false)}
          tenantId={tenantId!}
          defaultRole="tenant_member"
        />
      )}

      {device.notes && (
        <Card>
          <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-text-secondary whitespace-pre-wrap">{device.notes}</p></CardContent>
        </Card>
      )}

      {canDelete && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete this device</p>
                <p className="text-sm text-text-muted">Once deleted, this device and all its data will no longer be accessible.</p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
                <Trash2 className="mr-1 h-4 w-4" />
                Delete Device
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <DeleteResourceDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          deleteDevice.mutate(undefined, {
            onSuccess: () => navigate(`/tenants/${tenantId}/devices`),
          });
        }}
        isPending={deleteDevice.isPending}
        resourceName="Device"
      />
    </div>
  );
}

function hasVantaData(device: Device): boolean {
  return !!(
    device.is_managed ||
    device.auto_updates_enabled ||
    device.last_enrolled_at ||
    device.mac_profile ||
    device.security_center ||
    (device.applications && device.applications.length > 0) ||
    (device.programs && device.programs.length > 0) ||
    (device.browser_extensions && device.browser_extensions.length > 0) ||
    (device.mac_drives && device.mac_drives.length > 0) ||
    (device.windows_drives && device.windows_drives.length > 0) ||
    (device.user_device_policies && device.user_device_policies.length > 0) ||
    (device.screenlock_policies && device.screenlock_policies.length > 0) ||
    (device.security_products && device.security_products.length > 0)
  );
}

function BoolBadge({ value, label }: { value: boolean | null | undefined; label?: string }) {
  if (value == null) return <Badge variant="default">{label ?? "Unknown"}</Badge>;
  return <Badge variant={value ? "success" : "danger"}>{label ?? (value ? "Enabled" : "Disabled")}</Badge>;
}

function StatusBadge({ value }: { value: string }) {
  const variant = value === "GOOD" ? "success" : value === "POOR" || value === "ERROR" ? "danger" : "default";
  const label = value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  return <Badge variant={variant}>{label}</Badge>;
}

function VantaResourceCard({ device, tenantId, deviceId, vantaConnected }: { device: Device; tenantId: string; deviceId: string; vantaConnected: boolean }) {
  const [activeTab, setActiveTab] = useState("profile");
  const updateDevice = useUpdateDevice(tenantId, deviceId);

  // Modal state
  const [appModal, setAppModal] = useState<{ open: boolean; item?: DeviceApplication | null }>({ open: false });
  const [driveModal, setDriveModal] = useState<{ open: boolean; item?: MacDeviceDrive | null }>({ open: false });
  const [extModal, setExtModal] = useState<{ open: boolean; item?: DeviceBrowserExtension | null }>({ open: false });
  const [slModal, setSlModal] = useState<{ open: boolean; item?: DeviceScreenlockPolicy | null }>({ open: false });
  const [upModal, setUpModal] = useState<{ open: boolean; assignment?: DeviceUserAssignment; policy?: UserDevicePolicy | null }>({ open: false });
  const [progModal, setProgModal] = useState<{ open: boolean; item?: DeviceProgram | null }>({ open: false });
  const [winDriveModal, setWinDriveModal] = useState<{ open: boolean; item?: WindowsDeviceDrive | null }>({ open: false });
  const [spModal, setSpModal] = useState<{ open: boolean; item?: WindowsSecurityProduct | null }>({ open: false });
  const [scModal, setScModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: string } | null>(null);

  // Delete mutations
  const deleteApp = useDeleteApplication(tenantId, deviceId);
  const deleteDrive = useDeleteMacDrive(tenantId, deviceId);
  const deleteExt = useDeleteBrowserExtension(tenantId, deviceId);
  const deleteSl = useDeleteScreenlockPolicy(tenantId, deviceId);
  const deleteUp = useDeleteUserPolicy(tenantId, deviceId);
  const deleteProg = useDeleteProgram(tenantId, deviceId);
  const deleteWinDrive = useDeleteWindowsDrive(tenantId, deviceId);
  const deleteSp = useDeleteSecurityProduct(tenantId, deviceId);
  const deleteSc = useDeleteSecurityCenter(tenantId, deviceId);

  const handleDelete = () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    const onSuccess = { onSuccess: () => setDeleteTarget(null) };
    if (type === "application") deleteApp.mutate({ id }, onSuccess);
    else if (type === "drive") deleteDrive.mutate({ id }, onSuccess);
    else if (type === "extension") deleteExt.mutate({ id }, onSuccess);
    else if (type === "screenlock") deleteSl.mutate({ id }, onSuccess);
    else if (type === "user-policy") deleteUp.mutate({ id }, onSuccess);
    else if (type === "program") deleteProg.mutate({ id }, onSuccess);
    else if (type === "windows-drive") deleteWinDrive.mutate({ id }, onSuccess);
    else if (type === "security-product") deleteSp.mutate({ id }, onSuccess);
    else if (type === "security-center") deleteSc.mutate({ id }, onSuccess);
  };

  if (!hasVantaData(device)) return null;

  const isMac = device.platform?.toLowerCase().includes("mac") || device.platform?.toLowerCase().includes("darwin");
  const isWindows = device.platform?.toLowerCase().includes("windows") || device.platform?.toLowerCase().includes("win");

  const softwareItems = isMac ? device.applications ?? [] : device.programs ?? [];
  const drives = isMac ? device.mac_drives ?? [] : device.windows_drives ?? [];
  const tabItems = [
    { key: "profile", label: "Security Profile" },
    { key: "software", label: `Software (${softwareItems.length})` },
    { key: "drives", label: `Drives (${drives.length})` },
    { key: "users", label: `User Policies (${(device.user_assignments ?? []).length})` },
    { key: "extensions", label: `Extensions (${(device.browser_extensions ?? []).length})` },
    { key: "screenlock", label: `Screenlock (${(device.screenlock_policies ?? []).length})` },
    ...(isWindows ? [{ key: "security", label: `Security Products (${(device.security_products ?? []).length})` }] : []),
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Vanta Resource Data</CardTitle>
          {vantaConnected && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-muted">Send to Vanta</span>
              <Switch
                checked={device.send_to_vanta !== false}
                onChange={(checked) => updateDevice.mutate({ send_to_vanta: checked })}
                disabled={updateDevice.isPending}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status row */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <p className="text-xs font-medium text-text-muted uppercase mb-1">Managed</p>
            <BoolBadge value={device.is_managed} label={device.is_managed ? "Yes" : "No"} />
          </div>
          <div>
            <p className="text-xs font-medium text-text-muted uppercase mb-1">Auto Updates</p>
            <BoolBadge value={device.auto_updates_enabled} />
          </div>
          <div>
            <p className="text-xs font-medium text-text-muted uppercase mb-1">Last Enrolled</p>
            <p className="text-sm">{device.last_enrolled_at ? new Date(device.last_enrolled_at).toLocaleDateString() : "\u2014"}</p>
          </div>
        </div>

        {/* Tabbed element lists */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap">
            {tabItems.map((t) => (
              <TabsTrigger key={t.key} value={t.key} activeValue={activeTab} onClick={setActiveTab}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="mt-4 overflow-x-auto">
            {activeTab === "profile" && (
              <>
                {isWindows && (
                  <div className="flex justify-end mb-2">
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => setScModal(true)}>
                        {device.security_center ? "Edit" : "Set Up"} Security Center
                      </Button>
                      {device.security_center && (
                        <Button size="sm" variant="outline" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "security-center", id: device.security_center!.id })}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-4 text-left font-medium text-text-muted">Field</th>
                      <th className="py-2 px-4 text-left font-medium text-text-muted">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isMac && (
                      <tr className="border-b border-border/50">
                        <td className="py-2 pr-4">XProtect</td>
                        <td className="py-2 px-4">{device.mac_profile ? <BoolBadge value={device.mac_profile.is_xprotect_enabled} /> : "\u2014"}</td>
                      </tr>
                    )}
                    {isWindows && (
                      <>
                        <tr className="border-b border-border/50">
                          <td className="py-2 pr-4">Firewall</td>
                          <td className="py-2 px-4">{device.security_center ? <StatusBadge value={device.security_center.firewall} /> : "\u2014"}</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-2 pr-4">Antivirus</td>
                          <td className="py-2 px-4">{device.security_center ? <StatusBadge value={device.security_center.antivirus} /> : "\u2014"}</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-2 pr-4">Auto Update</td>
                          <td className="py-2 px-4">{device.security_center ? <StatusBadge value={device.security_center.autoupdate} /> : "\u2014"}</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-2 pr-4">Internet Setting</td>
                          <td className="py-2 px-4">{device.security_center ? <StatusBadge value={device.security_center.internet_setting} /> : "\u2014"}</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-2 pr-4">User Account Control</td>
                          <td className="py-2 px-4">{device.security_center ? <StatusBadge value={device.security_center.user_account_control} /> : "\u2014"}</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-2 pr-4">Security Center Service</td>
                          <td className="py-2 px-4">{device.security_center ? <StatusBadge value={device.security_center.security_center_service} /> : "\u2014"}</td>
                        </tr>
                      </>
                    )}
                    {!isMac && !isWindows && (
                      <tr><td colSpan={2} className="py-2 text-text-muted">No security profile data for this platform.</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {activeTab === "software" && (
              <>
                <div className="flex justify-end mb-2">
                  {isMac && <Button size="sm" onClick={() => setAppModal({ open: true })}>Add Application</Button>}
                  {isWindows && <Button size="sm" onClick={() => setProgModal({ open: true })}>Add Program</Button>}
                </div>
                {softwareItems.length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No software found.</p>
                ) : isMac ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-medium text-text-muted">Name</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Bundle ID</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Last Opened</th>
                        <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(device.applications ?? []).map((app) => (
                        <tr key={app.id} className="border-b border-border/50">
                          <td className="py-2 pr-4">{app.name}</td>
                          <td className="py-2 px-4 text-text-muted">{app.bundle_id || "\u2014"}</td>
                          <td className="py-2 px-4 text-text-muted">{app.last_opened_at ? new Date(app.last_opened_at).toLocaleDateString() : "\u2014"}</td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setAppModal({ open: true, item: app })}>Edit</Button>
                              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "application", id: app.id })}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-medium text-text-muted">Name</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Last Opened</th>
                        <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(device.programs ?? []).map((prog) => (
                        <tr key={prog.id} className="border-b border-border/50">
                          <td className="py-2 pr-4">{prog.name}</td>
                          <td className="py-2 px-4 text-text-muted">{prog.last_opened_at ? new Date(prog.last_opened_at).toLocaleDateString() : "\u2014"}</td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setProgModal({ open: true, item: prog })}>Edit</Button>
                              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "program", id: prog.id })}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {activeTab === "drives" && (
              <>
                <div className="flex justify-end mb-2">
                  {isMac && <Button size="sm" onClick={() => setDriveModal({ open: true })}>Add Drive</Button>}
                  {isWindows && <Button size="sm" onClick={() => setWinDriveModal({ open: true })}>Add Drive</Button>}
                </div>
                {drives.length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No drives found.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-medium text-text-muted">Name</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Encrypted</th>
                        {isMac && <th className="py-2 px-4 text-left font-medium text-text-muted">FileVault</th>}
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Boot Volume</th>
                        <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drives.map((d) => (
                        <tr key={d.id} className="border-b border-border/50">
                          <td className="py-2 pr-4">{d.name}</td>
                          <td className="py-2 px-4"><BoolBadge value={d.encrypted} /></td>
                          {isMac && <td className="py-2 px-4"><BoolBadge value={"filevault_enabled" in d ? (d as MacDeviceDrive).filevault_enabled : null} /></td>}
                          <td className="py-2 px-4">{d.is_boot_volume == null ? "\u2014" : d.is_boot_volume ? "Yes" : "No"}</td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              {isMac && <Button variant="outline" size="sm" onClick={() => setDriveModal({ open: true, item: d as MacDeviceDrive })}>Edit</Button>}
                              {isWindows && <Button variant="outline" size="sm" onClick={() => setWinDriveModal({ open: true, item: d as WindowsDeviceDrive })}>Edit</Button>}
                              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: isMac ? "drive" : "windows-drive", id: d.id })}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {activeTab === "users" && (() => {
              const assignments = device.user_assignments ?? [];
              const policyMap = new Map(
                (device.user_device_policies ?? []).map(p => [p.assignment_id, p])
              );
              return assignments.length === 0 ? (
                <p className="text-sm text-text-muted py-2">No users assigned to this device.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-4 text-left font-medium text-text-muted">Email</th>
                      <th className="py-2 px-4 text-left font-medium text-text-muted">Last Login</th>
                      <th className="py-2 px-4 text-left font-medium text-text-muted">Requires Password</th>
                      <th className="py-2 px-4 text-left font-medium text-text-muted">Sleep Timeout</th>
                      <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((ua) => {
                      const p = policyMap.get(ua.id);
                      return (
                        <tr key={ua.id} className="border-b border-border/50">
                          <td className="py-2 pr-4">{ua.email}</td>
                          <td className="py-2 px-4 text-text-muted">{p?.last_login_at ? new Date(p.last_login_at).toLocaleDateString() : "\u2014"}</td>
                          <td className="py-2 px-4">{p ? <BoolBadge value={p.screenlock_settings?.requiresPassword} label={p.screenlock_settings?.requiresPassword ? "Yes" : "No"} /> : "\u2014"}</td>
                          <td className="py-2 px-4">{p ? (p.screenlock_settings?.screenSleepTimeoutMs > 0 ? `${Math.round(p.screenlock_settings.screenSleepTimeoutMs / 60000)} min` : "Never") : "\u2014"}</td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setUpModal({ open: true, assignment: ua, policy: p ?? null })}>
                                {p ? "Edit" : "Add Policy"}
                              </Button>
                              {p && (
                                <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "user-policy", id: p.id })}>Delete</Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}

            {activeTab === "extensions" && (
              <>
                <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setExtModal({ open: true })}>Add Extension</Button></div>
                {(device.browser_extensions ?? []).length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No browser extensions found.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-medium text-text-muted">Name</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Browser</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Extension ID</th>
                        <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(device.browser_extensions ?? []).map((ext) => (
                        <tr key={ext.id} className="border-b border-border/50">
                          <td className="py-2 pr-4">{ext.name}</td>
                          <td className="py-2 px-4">{ext.browser}</td>
                          <td className="py-2 px-4 text-text-muted truncate max-w-[200px]">{ext.extension_id}</td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setExtModal({ open: true, item: ext })}>Edit</Button>
                              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "extension", id: ext.id })}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {activeTab === "screenlock" && (
              <>
                <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setSlModal({ open: true })}>Add Policy</Button></div>
                {(device.screenlock_policies ?? []).length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No screenlock policies found.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-medium text-text-muted">Requires Password</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Sleep Timeout</th>
                        <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(device.screenlock_policies ?? []).map((p) => (
                        <tr key={p.id} className="border-b border-border/50">
                          <td className="py-2 pr-4"><BoolBadge value={p.requires_password} label={p.requires_password ? "Yes" : "No"} /></td>
                          <td className="py-2 px-4">{p.screen_sleep_timeout_ms > 0 ? `${Math.round(p.screen_sleep_timeout_ms / 60000)} min` : "Never"}</td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setSlModal({ open: true, item: p })}>Edit</Button>
                              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "screenlock", id: p.id })}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {activeTab === "security" && (
              <>
                <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setSpModal({ open: true })}>Add Security Product</Button></div>
                {(device.security_products ?? []).length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No security products found.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-medium text-text-muted">Name</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Category</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">State</th>
                        <th className="py-2 px-4 text-left font-medium text-text-muted">Signatures</th>
                        <th className="py-2 px-4 text-right font-medium text-text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(device.security_products ?? []).map((sp) => (
                        <tr key={sp.id} className="border-b border-border/50">
                          <td className="py-2 pr-4">{sp.name}</td>
                          <td className="py-2 px-4">{sp.category}</td>
                          <td className="py-2 px-4"><Badge variant={sp.state === "ON" ? "success" : "danger"}>{sp.state}</Badge></td>
                          <td className="py-2 px-4"><BoolBadge value={sp.signatures_up_to_date} label={sp.signatures_up_to_date ? "Up to date" : "Outdated"} /></td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setSpModal({ open: true, item: sp })}>Edit</Button>
                              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={() => setDeleteTarget({ type: "security-product", id: sp.id })}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </Tabs>
      </CardContent>

      {/* Modals */}
      <ApplicationModal
        open={appModal.open}
        onClose={() => setAppModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        application={appModal.item}
      />
      <MacDriveModal
        open={driveModal.open}
        onClose={() => setDriveModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        drive={driveModal.item}
      />
      <BrowserExtensionModal
        open={extModal.open}
        onClose={() => setExtModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        extension={extModal.item}
      />
      <ScreenlockPolicyModal
        open={slModal.open}
        onClose={() => setSlModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        policy={slModal.item}
      />
      <UserPolicyModal
        open={upModal.open}
        onClose={() => setUpModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        assignmentId={upModal.assignment?.id ?? ""}
        userEmail={upModal.assignment?.email ?? ""}
        policy={upModal.policy}
      />
      <ProgramModal
        open={progModal.open}
        onClose={() => setProgModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        program={progModal.item}
      />
      <WindowsDriveModal
        open={winDriveModal.open}
        onClose={() => setWinDriveModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        drive={winDriveModal.item}
      />
      <SecurityProductModal
        open={spModal.open}
        onClose={() => setSpModal({ open: false })}
        tenantId={tenantId}
        deviceId={deviceId}
        product={spModal.item}
      />
      <SecurityCenterModal
        open={scModal}
        onClose={() => setScModal(false)}
        tenantId={tenantId}
        deviceId={deviceId}
        securityCenter={device.security_center}
      />
      <DeleteResourceDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isPending={deleteApp.isPending || deleteDrive.isPending || deleteExt.isPending || deleteSl.isPending || deleteUp.isPending || deleteProg.isPending || deleteWinDrive.isPending || deleteSp.isPending || deleteSc.isPending}
        resourceName={deleteTarget?.type ?? "item"}
      />
    </Card>
  );
}
